import { useCallback } from 'react';
import { Connection, Edge, Node, OnConnect } from 'reactflow';
import { useNotifications } from '../../contexts/NotificationContext';
import { CanvasMode } from './index';
import useDefaultEdgeStore from '../../store/useDefaultEdgeStore'; // Import the default edge store

// Define a type for the required props from the store
export type ConnectionHandlerProps = { // Export the type
    nodes: Node[];
    edges: Edge[];
    setEdges: (edges: Edge[]) => void;
    recalculateClusterLatencies?: () => void; // Optional project-specific action
};

export const useConnectionHandlers = (
    isEmbedded: boolean,
    takeSnapshot: () => void,
    mode: CanvasMode,
    props: ConnectionHandlerProps // Use the props type
): { onConnect: OnConnect } => {
    const { showNotification } = useNotifications();
    // Use actions/state from the passed props object
    const { nodes: allNodes, edges: allEdges, setEdges, recalculateClusterLatencies } = props;

    const onConnect: OnConnect = useCallback((params: Connection) => {
        // ... (rest of the onConnect logic remains the same, using allNodes, allEdges, setEdges, recalculateClusterLatencies from props) ...
         if (isEmbedded) return;

        // Ensure source and target are valid strings
        if (!params.source || !params.target) {
            console.error("[Canvas] Invalid connection parameters:", params);
            showNotification('error', 'Invalid connection attempt', 3000);
            return;
        }

        // --- Validation ---
        // 1. Duplicate Edge Check
        const duplicateEdge = allEdges.find(
            edge => edge.source === params.source && edge.target === params.target
        );
        if (duplicateEdge) {
            showNotification('error', 'An edge already exists between these nodes', 3000);
            return;
        }

        // 2. Self-Loop Check
        if (params.source === params.target) {
            showNotification('error', 'Cannot connect a node to itself', 3000);
            return;
        }

        // 3. Destination-to-Destination Check (moved inside mode check)
        const sourceNode = allNodes.find(n => n.id === params.source);
        const targetNode = allNodes.find(n => n.id === params.target);

        // --- Playbook Mode Specific Validation ---
        if (mode === 'playbook') {
            // TODO: Implement detailed playbook connection rules
            if (targetNode?.type === 'playbook-root') {
                showNotification('error', 'Playbook Root cannot receive connections.', 3000);
                return;
            }
            console.log(`[Playbook Mode] Connection allowed between ${sourceNode?.type} and ${targetNode?.type}`);
        }
        // --- Project Mode Specific Validation ---
        else if (mode === 'project') {
            if (sourceNode?.type === 'destination' && targetNode?.type === 'destination') {
                showNotification('error', 'Cannot connect destination nodes to each other', 3000);
                return;
            }
            // Add other project-specific rules here if needed
        }


        // --- Handle Assignment ---
        let sourceHandle = params.sourceHandle;
        if (mode === 'project' && sourceNode?.type === 'group' && (!sourceHandle || sourceHandle === '')) {
            sourceHandle = `${sourceNode.id}-source`;
        } else if (sourceHandle === null || sourceHandle === undefined) {
            sourceHandle = null;
        }

        let targetHandle = params.targetHandle;
        if (mode === 'project' && targetNode?.type === 'group' && (!targetHandle || targetHandle === '')) {
            targetHandle = `${targetNode.id}-target`;
        } else if (targetHandle === null || targetHandle === undefined) {
            targetHandle = null;
        }


        // --- Create and Add Edge ---
        takeSnapshot();

        // Get default edge properties from the store
        const { defaultEdgeData, defaultEdgeStyle, defaultMarkerStart, defaultMarkerEnd, defaultAnimated, defaultType } = useDefaultEdgeStore.getState();

        const newEdge: Edge = {
            ...params,
            source: params.source,
            target: params.target,
            id: `edge-${params.source}-${params.target}`,
            type: defaultType, // Use default type
            animated: defaultAnimated, // Use default animated
            sourceHandle: sourceHandle,
            targetHandle: targetHandle,
            style: defaultEdgeStyle, // Use default style
            data: { // Use default data and merge with any existing params data
                ...defaultEdgeData,
            },
            markerStart: defaultMarkerStart, // Use default marker start
            markerEnd: defaultMarkerEnd, // Use default marker end
        };

        setEdges([...allEdges, newEdge]);

        if (mode === 'project' && recalculateClusterLatencies) {
            setTimeout(() => {
                recalculateClusterLatencies();
            }, 0);
        }

    }, [
        isEmbedded,
        allEdges, // from props
        allNodes, // from props
        setEdges, // from props
        showNotification,
        takeSnapshot,
        recalculateClusterLatencies, // from props
        mode
    ]);

    return { onConnect };
};
